import { api } from '@/store/api'
import type { Gate, StudioJob } from './studioTypes'
import type {
  CancelledJob,
  JobResumed,
  JobStarted,
  Quote,
  StartBlockEdit,
  StartCourseGeneration,
  StartImageGeneration,
  StartTranslation,
} from './studioStartTypes'

// getStudioJob намеренно отдельный от getAiJob (features/ai/aiApi.ts): тот
// типизирован под джобу перевода СТАТЬИ, у неё другой result, и ломать
// работающий перевод инструкций ради студии незачем. Адрес общий — это
// одна ручка бэка на все типы джоб.
export const studioApi = api.injectEndpoints({
  endpoints: (build) => ({
    startCourseGeneration: build.mutation<JobStarted, StartCourseGeneration>({
      query: (body) => ({ url: 'ai/studio/course-generations', method: 'POST', body }),
    }),
    startImageGeneration: build.mutation<JobStarted, StartImageGeneration>({
      query: (body) => ({ url: 'ai/studio/image-generations', method: 'POST', body }),
    }),
    startBlockEdit: build.mutation<JobStarted, StartBlockEdit>({
      query: (body) => ({ url: 'ai/studio/block-edits', method: 'POST', body }),
    }),
    startTranslation: build.mutation<JobStarted, StartTranslation>({
      query: (body) => ({ url: 'ai/studio/translations', method: 'POST', body }),
    }),
    getQuote: build.query<Quote, number>({
      query: (courseId) => `ai/studio/courses/${courseId}/quote`,
      // Смета несёт `active_job` и `pending_units` — оба протухают, когда
      // JobPoller инвалидирует курс на терминальном статусе прогона.
      providesTags: (_r, _e, courseId) => [{ type: 'Quote', id: courseId }],
    }),
    getStudioJob: build.query<StudioJob, number>({
      query: (jobId) => `ai/jobs/${jobId}`,
    }),
    resumeJob: build.mutation<JobResumed, number>({
      // Тело пустое: изменить задание — это новый прогон, а не продолжение.
      // Инвалидацию тегов здесь не ставим: продолжение ничего в курсе ещё не
      // изменило — перецепление реестра на новую джобу делает другая задача.
      query: (jobId) => ({ url: `ai/studio/jobs/${jobId}/resume`, method: 'POST' }),
    }),
    cancelJob: build.mutation<CancelledJob, number>({
      query: (jobId) => ({ url: `ai/studio/jobs/${jobId}/cancel`, method: 'POST' }),
      // Статус патчим ОТВЕТОМ ручки, как у `answerGate`: иначе до следующего
      // тика поллера (до 3 с) блок хода работы показывал бы живую полосу
      // отменённого прогона, а кнопка «Отменить» оставалась бы кликабельной.
      // `cancelled` терминален — поллер сам погасит опрос и инвалидирует курс.
      //
      // Патч не последнее слово: тик опроса, ушедший ДО отмены, мог ещё висеть
      // в сети — его ответ `running` лёг бы поверх патча, а поллер к тому
      // моменту уже погасил опрос по `cancelled`, и полоса застряла бы навсегда.
      // Ждём такой тик и перечитываем джобу принудительно: новый запрос уходит
      // заведомо после отмены и несёт правду сервера. Ждать обязательно —
      // RTK Query не пускает второй запрос, пока первый в полёте.
      async onQueryStarted(jobId, { dispatch, queryFulfilled }) {
        let charged: CancelledJob['credits_charged']
        try {
          charged = (await queryFulfilled).data.credits_charged
        } catch {
          // Отказ разбирает `useCancelTranslationRun` через `.unwrap()`.
          return
        }
        dispatch(
          studioApi.util.updateQueryData('getStudioJob', jobId, (draft) => {
            draft.status = 'cancelled'
            if (draft.result) draft.result.credits_charged = charged
          }),
        )
        await dispatch(studioApi.util.getRunningQueryThunk('getStudioJob', jobId))
        void dispatch(
          studioApi.endpoints.getStudioJob.initiate(jobId, {
            subscribe: false,
            forceRefetch: true,
          }),
        )
      },
    }),
    answerGate: build.mutation<
      Gate,
      { jobId: number; gateId: number; option_key: string | null; text: string | null }
    >({
      query: ({ gateId, option_key, text }) => ({
        url: `ai/studio/gates/${gateId}/answer`,
        method: 'POST',
        body: { option_key, text },
      }),
      // Первая декларативная инвалидация в этом файле (образец —
      // `features/editor/lessonsApi.ts:32`): ответ меняет КУРС раньше
      // терминального статуса прогона — выбранная картинка уже в своём месте,
      // `imageStyle` и `styleReferences` тоже другие. Инвалидация поллера тут
      // не помогает: она случится только в конце прогона, а автор смотрит на
      // курс сейчас. Теги без `id` — гейт не знает, какой курс правит.
      invalidatesTags: [{ type: 'Course' }, { type: 'Lesson' }, { type: 'Block' }],
      // Important 1 (финальное ревью): мутация получает от сервера готовый
      // `Gate`, но раньше его выбрасывала. `getStudioJob` не несёт
      // `providesTags` (см. выше), а инвалидация целит курс/уроки/блоки, не
      // саму джобу — без патча кеша `gate.state` в кеше джобы оставался бы
      // `pending` до следующего тика `JobPoller` (до 3 с): карточка снова
      // становилась кликабельной и молчала, что ответ уже ушёл. Второй клик
      // в этом окне уходил бы по ДРУГОМУ варианту — сервер идемпотентен и
      // молча оставляет ПЕРВЫЙ выбор, а автор платит за прогон в стиле,
      // который считал заменённым. Патчим ОТВЕТОМ САМОЙ РУЧКИ: источник
      // правды прежний (сервер), просто доставленный на тик раньше, а не
      // вычисленный из `expires_at` или локального таймера.
      async onQueryStarted({ jobId, gateId }, { dispatch, queryFulfilled }) {
        try {
          const { data: answered } = await queryFulfilled
          dispatch(
            studioApi.util.updateQueryData('getStudioJob', jobId, (draft) => {
              const i = (draft.gates ?? []).findIndex((g) => g.id === gateId)
              if (i >= 0) draft.gates![i] = answered
            }),
          )
        } catch {
          // Отказ мутации разбирает `useAnswerGate` через `.unwrap()` —
          // здесь патчить нечего, кеш остаётся прежним.
        }
      },
    }),
  }),
})

export const {
  useStartCourseGenerationMutation,
  useStartImageGenerationMutation,
  useStartBlockEditMutation,
  useStartTranslationMutation,
  useGetQuoteQuery,
  useGetStudioJobQuery,
  useAnswerGateMutation,
  useResumeJobMutation,
  useCancelJobMutation,
} = studioApi
